// File: /var/www/app2/frontend/src/components/SupporterManagement.tsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { User } from "../App";
import "../styles/SupporterManagement.css";

interface SupporterManagementProps {
  user: User;
}

interface Missionary {
  id: number;
  name: string;
  number: string;
  state?: string;
  location?: string;
}

interface Supporter {
  id: number;
  username: string;
  full_name?: string;
  email?: string;
  organization?: string;
  assignedMissionaries: Missionary[];
}

function SupporterManagement({ user }: SupporterManagementProps) {
  const [supporters, setSupporters] = useState<Supporter[]>([]);
  const [missionaries, setMissionaries] = useState<Missionary[]>([]);
  const [selectedSupporter, setSelectedSupporter] = useState<Supporter | null>(null);
  const [selectedMissionaries, setSelectedMissionaries] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchSupporters();
    fetchMissionaries();
  }, []);

  const fetchSupporters = async () => {
    try {
      const response = await axios.get("/api/supporter-management");
      setSupporters(response.data.supporters || []);
      setError("");
    } catch (error) {
      console.error("Failed to fetch supporters:", error);
      setError("Failed to load supporters");
    } finally {
      setLoading(false);
    }
  };

  const fetchMissionaries = async () => {
    try {
      const response = await axios.get("/api/missionaries");
      setMissionaries(response.data.missionaries || []);
    } catch (error) {
      console.error("Failed to fetch missionaries:", error);
    }
  };

  const handleSelectSupporter = (supporter: Supporter) => {
    setSelectedSupporter(supporter);
    const assignedIds = supporter.assignedMissionaries.map((m) => m.id);
    setSelectedMissionaries(assignedIds);
  };

  const handleToggleMissionary = (missionaryId: number) => {
    if (selectedMissionaries.includes(missionaryId)) {
      setSelectedMissionaries(
        selectedMissionaries.filter((id) => id !== missionaryId)
      );
    } else {
      setSelectedMissionaries([...selectedMissionaries, missionaryId]);
    }
  };

  const handleSaveAssignments = async () => {
    if (!selectedSupporter) return;

    setSaving(true);
    try {
      await axios.post(
        `/api/supporter-management/${selectedSupporter.id}/bulk-assign`,
        {
          missionaryIds: selectedMissionaries,
        }
      );

      // Refresh supporters list
      await fetchSupporters();

      // Update selected supporter
      const updatedSupporter = supporters.find(
        (s) => s.id === selectedSupporter.id
      );
      if (updatedSupporter) {
        const response = await axios.get(
          `/api/supporter-management/${selectedSupporter.id}/assignments`
        );
        updatedSupporter.assignedMissionaries = response.data.assignments || [];
        setSelectedSupporter(updatedSupporter);
      }

      alert("Assignments saved successfully!");
    } catch (error: any) {
      console.error("Failed to save assignments:", error);
      alert(
        "Failed to save assignments: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = () => {
    setSelectedMissionaries(missionaries.map((m) => m.id));
  };

  const handleDeselectAll = () => {
    setSelectedMissionaries([]);
  };

  const filteredMissionaries = missionaries.filter(
    (missionary) =>
      missionary.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      missionary.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      missionary.state?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="loading">Loading supporter management...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="supporter-management-container">
      <h2>Manage Supporters</h2>
      <p className="page-description">
        Assign missionaries to supporters. Supporters can only view approved
        reports for their assigned missionaries.
      </p>

      <div className="management-layout">
        {/* Supporters List */}
        <div className="supporters-panel">
          <h3>Supporters ({supporters.length})</h3>
          {supporters.length === 0 ? (
            <p className="no-data">No supporters found</p>
          ) : (
            <div className="supporters-list">
              {supporters.map((supporter) => (
                <div
                  key={supporter.id}
                  className={`supporter-item ${
                    selectedSupporter?.id === supporter.id ? "selected" : ""
                  }`}
                  onClick={() => handleSelectSupporter(supporter)}
                >
                  <div className="supporter-name">
                    {supporter.full_name || supporter.username}
                  </div>
                  <div className="supporter-info">
                    {supporter.email && (
                      <div className="supporter-email">{supporter.email}</div>
                    )}
                    {supporter.organization && (
                      <div className="supporter-org">
                        {supporter.organization}
                      </div>
                    )}
                  </div>
                  <div className="assignment-count">
                    {supporter.assignedMissionaries.length} missionary
                    {supporter.assignedMissionaries.length !== 1 ? "ies" : "y"}{" "}
                    assigned
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assignment Editor */}
        <div className="assignment-panel">
          {selectedSupporter ? (
            <>
              <div className="panel-header">
                <h3>
                  Assign Missionaries to {selectedSupporter.full_name || selectedSupporter.username}
                </h3>
                <div className="bulk-actions">
                  <button
                    onClick={handleSelectAll}
                    className="bulk-btn"
                    disabled={saving}
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleDeselectAll}
                    className="bulk-btn"
                    disabled={saving}
                  >
                    Deselect All
                  </button>
                  <button
                    onClick={handleSaveAssignments}
                    className="save-btn"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Assignments"}
                  </button>
                </div>
              </div>

              <div className="selected-count">
                {selectedMissionaries.length} of {missionaries.length}{" "}
                missionaries selected
              </div>

              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search missionaries by name, number, or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="missionaries-checklist">
                {filteredMissionaries.length === 0 ? (
                  <p className="no-data">No missionaries found</p>
                ) : (
                  filteredMissionaries.map((missionary) => (
                    <label
                      key={missionary.id}
                      className="missionary-checkbox-item"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMissionaries.includes(missionary.id)}
                        onChange={() => handleToggleMissionary(missionary.id)}
                        disabled={saving}
                      />
                      <div className="missionary-checkbox-details">
                        <div className="missionary-checkbox-name">
                          {missionary.name}
                        </div>
                        <div className="missionary-checkbox-info">
                          #{missionary.number} • {missionary.state || "Unknown"}{" "}
                          • {missionary.location || "Unknown"}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>Select a supporter from the list to manage their missionary assignments</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupporterManagement;
